/** @jsx jsx */

import { ComponentProps, PropsWithChildren } from 'react';

import { css, jsx } from '@emotion/core';
import { Colors, Icon, IconName, Margin } from '@zapier/design-system';

import Link from 'app/common/components/Link';

const Styles = {
  root: css`
    :hover {
      background-color: ${Colors.GrayWarm3};
    }
  `,
  active: css`
    background-color: ${Colors.Blue3};
    color: ${Colors.PrimeWhite};
    font-weight: 600;
    :hover {
      background-color: ${Colors.Blue3};
    }
  `,
  linkContent: (gridTemplateColumn?: string, isLocked?: boolean) => css`
    display: grid;
    align-items: center;
    gap: ${isLocked ? 5 : 10}px;
    padding: 10px 40px;
    grid-template-columns: ${gridTemplateColumn
      ? gridTemplateColumn
      : isLocked
      ? '20px auto'
      : 'auto 1fr'};
  `,
};

export type Props = PropsWithChildren<{
  /** Apply a diferent column layout. Useful when children has more than one child */
  gridTemplateColumn?: string;
  /** The URL clicking the link will direct the user to. */
  href: string;
  /** The type of icon to render in front of the link text. */
  icon?: IconName;
  /** Is this the current active link. */
  isActive?: boolean;
  /** Is this link locked/disabled */
  isLocked?: boolean;
  /** Target of this link */
  target?: ComponentProps<typeof Link>['target'];
}>;

/**
 * Renders a NavLink to be used with this component.
 * Display colors vary depending on whether the link is active,
 * or is disabled.
 *
 * @param props
 */
function NavLink({
  children,
  gridTemplateColumn,
  icon,
  href,
  isLocked,
  isActive,
  target,
}: Props) {
  const hasIcon = !!(icon || isLocked);

  const content = (
    <div css={Styles.linkContent(gridTemplateColumn, isLocked)}>
      {hasIcon &&
        (isLocked ? (
          <Icon name="actionLockFill" size={20} />
        ) : (
          <Margin margin="4px 0px 0px 0px">
            <Icon
              color={isActive ? 'PrimeWhite' : 'inherit'}
              name={icon}
              size={20}
            />
          </Margin>
        ))}
      {children}
    </div>
  );

  return (
    <li css={[Styles.root, isActive && Styles.active]}>
      {isLocked ? (
        <div style={{ color: Colors.GrayWarm7, cursor: 'not-allowed' }}>
          {content}
        </div>
      ) : (
        <Link
          color="inherit"
          href={href}
          style={{ cursor: 'pointer' }}
          target={target}
        >
          {content}
        </Link>
      )}
    </li>
  );
}

export default NavLink;
